'use client';

import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Megaphone, MessageSquare, Users, UserCog, Calendar } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const audienceLabels: Record<string, { label: string; icon: typeof Users; color: string }> = {
  all_parents: { label: 'All Parents', icon: Users, color: 'bg-green-100 text-green-700' },
  all_coaches: { label: 'All Coaches', icon: UserCog, color: 'bg-blue-100 text-blue-700' },
  all_members: { label: 'Everyone', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
  session_attendees: { label: 'Session Update', icon: Calendar, color: 'bg-amber-100 text-amber-700' },
};

const priorityColors: Record<string, string> = {
  low: 'border-l-gray-300',
  normal: 'border-l-green-400',
  high: 'border-l-amber-500',
  urgent: 'border-l-red-500',
};

export default function ParentAnnouncementsPage() {
  const announcementsData = useQuery(api.parent.announcements.list, { limit: 50 });
  const announcements = announcementsData?.announcements ?? [];
  const isLoading = announcementsData === undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Megaphone className="w-8 h-8 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Announcements</h1>
          <p className="text-sm text-muted-foreground">Messages from your academy</p>
        </div>
      </div>

      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No announcements yet</p>
            <p className="text-sm text-muted-foreground">Announcements from your academy will appear here</p>
          </div>
        ) : (
          announcements.map((announcement) => {
            const audience = audienceLabels[announcement.targetAudience] || audienceLabels.all_members;
            const AudienceIcon = audience.icon;
            return (
              <div
                key={announcement.id}
                className={cn(
                  'bg-card rounded-lg shadow p-4 border-l-4',
                  priorityColors[announcement.priority] || priorityColors.normal
                )}
              >
                <div className="flex items-start gap-2 mb-2">
                  <h3 className="font-semibold text-foreground">{announcement.title}</h3>
                  <span className={cn('px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1', audience.color)}>
                    <AudienceIcon className="w-3 h-3" />
                    {audience.label}
                  </span>
                  {announcement.priority === 'urgent' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Urgent</span>
                  )}
                  {announcement.priority === 'high' && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Important</span>
                  )}
                </div>
                <p className="text-foreground whitespace-pre-wrap">{announcement.content}</p>
                <div className="mt-3 text-xs text-muted-foreground">
                  {announcement.createdByName
                    ? <span>From {announcement.createdByName} · </span>
                    : announcement.createdByEmail
                      ? <span>From {announcement.createdByEmail} · </span>
                      : null}
                  {new Date(announcement.createdAt).toLocaleDateString('en-AU', {
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
